import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Loader2, Plus, StickyNote, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { toast } from "sonner";

const PRIORITY_LABEL = { baja: "Baja", media: "Media", alta: "Alta" };
const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toIsoDate(year, monthIndex, day) {
  const m = String(monthIndex + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

function isDueAlert(note) {
  if (!note || note.status !== "pending" || !note.alert_enabled || !note.due_date) return false;
  return String(note.due_date).slice(0, 10) <= todayIso();
}

function buildMonthCells(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // Monday-first: JS getDay() Sun=0 → shift
  const startPad = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ day, iso: toIsoDate(year, monthIndex, day) });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Notas personales del auxiliar — solo BNT Asistente (no Despacho/Calendario).
 */
export default function BntAsistenteMisNotasPanel({ onNotesChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [priority, setPriority] = useState("media");
  const [selectedDay, setSelectedDay] = useState("");
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const onNotesChangedRef = useRef(onNotesChanged);
  const loadSeqRef = useRef(0);

  useEffect(() => {
    onNotesChangedRef.current = onNotesChanged;
  }, [onNotesChanged]);

  const reload = useCallback(async ({ notify = false } = {}) => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const { data } = await api.get("/asistente/personal-notes", { timeout: 20000 });
      if (seq !== loadSeqRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      if (notify) onNotesChangedRef.current?.(data);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      toast.error(formatApiError(err) || "No se pudieron cargar las notas");
      setItems([]);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload({ notify: false });
  }, [reload]);

  const createNote = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if (!text) {
      toast.error("Escribe el texto de la nota");
      return;
    }
    setSaving(true);
    try {
      await api.post("/asistente/personal-notes", {
        body: text,
        due_date: dueDate || null,
        alert_enabled: Boolean(dueDate) && alertEnabled,
        priority,
      });
      setBody("");
      setDueDate("");
      setAlertEnabled(true);
      setPriority("media");
      toast.success("Nota guardada");
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo guardar la nota");
    } finally {
      setSaving(false);
    }
  };

  const markDone = async (note) => {
    try {
      await api.patch(`/asistente/personal-notes/${note.id}`, { status: "done" });
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo marcar como hecha");
    }
  };

  const reopen = async (note) => {
    try {
      await api.patch(`/asistente/personal-notes/${note.id}`, { status: "pending" });
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo reabrir la nota");
    }
  };

  const remove = async (note) => {
    try {
      await api.delete(`/asistente/personal-notes/${note.id}`);
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo eliminar");
    }
  };

  const pending = items.filter((n) => n.status === "pending");
  const done = items.filter((n) => n.status === "done");

  const notesByDay = useMemo(() => {
    const map = {};
    pending.forEach((note) => {
      const day = String(note.due_date || "").slice(0, 10);
      if (!day) return;
      if (!map[day]) map[day] = [];
      map[day].push(note);
    });
    return map;
  }, [pending]);

  const monthCells = useMemo(
    () => buildMonthCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const monthLabel = useMemo(() => {
    const label = new Date(viewYear, viewMonth, 1).toLocaleDateString("es-MX", {
      month: "long",
      year: "numeric",
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }, [viewYear, viewMonth]);

  const selectedNotes = selectedDay ? notesByDay[selectedDay] || [] : [];
  const today = todayIso();

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const pickDay = (iso) => {
    setSelectedDay(iso);
    setDueDate(iso);
    setAlertEnabled(true);
  };

  return (
    <div className="space-y-3" data-testid="bnt-asistente-panel-mis-notas">
      <p className="text-[11px] text-slate-600 m-0 leading-relaxed">
        Notas y seguimientos <strong>solo suyos</strong>. Si pone fecha y activa el aviso, aparecerán en{" "}
        <strong>Alertas</strong> del asistente ese día (sin afectar Despacho ni Calendario).
      </p>

      <form
        onSubmit={createNote}
        className="rounded-sm border border-slate-200 bg-white p-3 space-y-2"
        data-testid="bnt-asistente-nota-form"
      >
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Nueva nota</Label>
        <SpellcheckTextarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Ej. Llamar al cliente X / revisar expediente…"
          className="w-full rounded-sm border border-slate-200 px-2 py-1.5 text-sm text-slate-900 resize-y min-h-[72px]"
          testId="bnt-asistente-nota-body"
          enableSpellcheck
          showSuggestionBar
        />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-slate-500">Fecha aviso</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                if (e.target.value) setSelectedDay(e.target.value);
              }}
              className="mt-0.5 h-8 text-xs"
              data-testid="bnt-asistente-nota-due"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Prioridad</Label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="mt-0.5 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs"
              data-testid="bnt-asistente-nota-priority"
            >
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-[11px] text-slate-700">
          <Checkbox
            checked={alertEnabled && Boolean(dueDate)}
            disabled={!dueDate}
            onCheckedChange={(v) => setAlertEnabled(Boolean(v))}
            data-testid="bnt-asistente-nota-alert"
          />
          Avisarme en Alertas ese día
        </label>
        <Button
          type="submit"
          disabled={saving || !body.trim()}
          className="h-8 w-full bg-[#0F2D5C] hover:bg-[#0b2249] text-white text-xs"
          data-testid="bnt-asistente-nota-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          Guardar nota
        </Button>
      </form>

      <div
        className="rounded-sm border border-slate-200 bg-white p-3 space-y-2"
        data-testid="bnt-asistente-notas-calendar"
      >
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-slate-200 text-slate-600"
            onClick={() => shiftMonth(-1)}
            aria-label="Mes anterior"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <p className="text-[11px] font-semibold text-[#0F2D5C] m-0">{monthLabel}</p>
          <button
            type="button"
            className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-slate-200 text-slate-600"
            onClick={() => shiftMonth(1)}
            aria-label="Mes siguiente"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {WEEKDAYS.map((d) => (
            <span key={d} className="text-[9px] text-slate-400 py-0.5">
              {d}
            </span>
          ))}
          {monthCells.map((cell, idx) => {
            if (!cell) {
              return <span key={`e-${idx}`} className="h-7" />;
            }
            const count = notesByDay[cell.iso]?.length || 0;
            const isToday = cell.iso === today;
            const isSelected = cell.iso === selectedDay;
            return (
              <button
                key={cell.iso}
                type="button"
                onClick={() => pickDay(cell.iso)}
                title={count ? `${count} nota(s)` : cell.iso}
                className={`relative h-7 rounded-sm text-[10px] leading-none ${
                  isSelected
                    ? "bg-[#0F2D5C] text-white"
                    : isToday
                      ? "bg-slate-100 text-[#0F2D5C] font-semibold"
                      : "text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`bnt-asistente-cal-day-${cell.iso}`}
              >
                {cell.day}
                {count > 0 ? (
                  <span
                    className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${
                      isSelected ? "bg-amber-300" : "bg-amber-500"
                    }`}
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-500 m-0">
          Punto ámbar = nota pendiente ese día. Clic elige la fecha del aviso.
        </p>
        {selectedDay ? (
          <div className="rounded-sm border border-slate-100 bg-slate-50/80 px-2 py-1.5 space-y-1">
            <p className="text-[10px] font-semibold text-slate-600 m-0">
              {selectedDay}
              {selectedNotes.length ? ` · ${selectedNotes.length} pendiente(s)` : " · sin pendientes"}
            </p>
            {selectedNotes.slice(0, 4).map((note) => (
              <p key={note.id} className="text-[10px] text-slate-700 m-0 truncate">
                · {note.body}
              </p>
            ))}
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="text-xs text-slate-500">Cargando notas…</p>
      ) : (
        <>
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 m-0">
              Pendientes ({pending.length})
            </p>
            {!pending.length ? (
              <p className="text-xs text-slate-500 m-0 flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5" /> Sin notas pendientes.
              </p>
            ) : (
              <ul className="space-y-2 m-0 p-0 list-none">
                {pending.map((note) => (
                  <li
                    key={note.id}
                    className={`rounded-sm border bg-white px-3 py-2 ${
                      isDueAlert(note) ? "border-amber-300 bg-amber-50/60" : "border-slate-200"
                    }`}
                    data-testid={`bnt-asistente-nota-item-${note.id}`}
                  >
                    <p className="text-sm text-slate-900 m-0 whitespace-pre-wrap">{note.body}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                      {note.due_date ? (
                        <span>
                          {isDueAlert(note) ? "Aviso desde" : "Fecha"} {String(note.due_date).slice(0, 10)}
                          {note.alert_enabled ? " · alerta" : ""}
                        </span>
                      ) : (
                        <span>Sin fecha</span>
                      )}
                      <span>· {PRIORITY_LABEL[note.priority] || "Media"}</span>
                    </div>
                    <div className="mt-2 flex gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px]"
                        onClick={() => markDone(note)}
                      >
                        <Check className="w-3 h-3 mr-1" /> Hecha
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10px] border-red-200 text-red-700"
                        onClick={() => remove(note)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {done.length > 0 ? (
            <div className="space-y-2 pt-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 m-0">
                Hechas ({done.length})
              </p>
              <ul className="space-y-1.5 m-0 p-0 list-none">
                {done.slice(0, 8).map((note) => (
                  <li
                    key={note.id}
                    className="rounded-sm border border-slate-100 bg-slate-50/80 px-2.5 py-1.5 flex items-start justify-between gap-2"
                  >
                    <p className="text-[11px] text-slate-500 m-0 line-through">{note.body}</p>
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        className="text-[10px] text-[#0F2D5C] underline"
                        onClick={() => reopen(note)}
                      >
                        Reabrir
                      </button>
                      <button
                        type="button"
                        className="text-[10px] text-red-600 underline"
                        onClick={() => remove(note)}
                      >
                        Borrar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
